/**
 * Verificação pública de um documento assinado — o endereço do QR code impresso no protocolo.
 *
 * Mostra o que o próprio protocolo já mostra (título, empresa, quem assinou com nome resumido,
 * quando) e o hash do arquivo lacrado. Quem tem o PDF em mãos confere se ele é o original: o hash
 * é calculado aqui no navegador, e o arquivo não sai do aparelho.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Divider, Paper, Stack, TextField, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import assinaturaService from '../../services/assinaturaService';
import {
  COR_DO_DOCUMENTO,
  COR_DO_SIGNATARIO,
  ROTULO_DO_DOCUMENTO,
  ROTULO_DO_SIGNATARIO,
  type Verificacao,
} from '../../types/assinatura';

async function sha256DoArquivo(arquivo: File): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await arquivo.arrayBuffer());
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

const Hash: React.FC<{ rotulo: string; valor: string }> = ({ rotulo, valor }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {rotulo}
    </Typography>
    <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
      {valor}
    </Typography>
  </Box>
);

export const VerificarAssinaturaPage: React.FC = () => {
  const { codigo: codigoDaRota = '' } = useParams<{ codigo: string }>();
  const navigate = useNavigate();

  const [codigo, setCodigo] = useState(codigoDaRota);
  const [dados, setDados] = useState<Verificacao | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [buscando, setBuscando] = useState(false);
  const [conferencia, setConferencia] = useState<{ nome: string; confere: boolean } | null>(null);

  const buscar = useCallback(async (alvo: string) => {
    if (!alvo.trim()) return;
    setErro(null);
    setConferencia(null);
    setBuscando(true);
    try {
      setDados(await assinaturaService.verificar(alvo.trim()));
    } catch (err: unknown) {
      setDados(null);
      setErro(err instanceof Error ? err.message : 'Nenhum documento com este código de verificação.');
    } finally {
      setBuscando(false);
    }
  }, []);

  useEffect(() => {
    setCodigo(codigoDaRota);
    void buscar(codigoDaRota);
  }, [codigoDaRota, buscar]);

  const conferir = useCallback(
    async (arquivo: File | undefined) => {
      if (!arquivo || !dados?.hashFinal) return;
      const hash = await sha256DoArquivo(arquivo);
      setConferencia({ nome: arquivo.name, confere: hash === dados.hashFinal });
    },
    [dados]
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: { xs: 2, sm: 4 }, px: { xs: 1.5, sm: 2 } }}>
      <Paper sx={{ maxWidth: 680, mx: 'auto', p: { xs: 2.5, sm: 4 } }}>
        <Typography variant="h5" gutterBottom>
          Verificar documento assinado
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Informe o código de verificação impresso no protocolo de assinatura.
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
          <TextField
            label="Código de verificação"
            placeholder="ABCD-2345-EFGH"
            value={codigo}
            onChange={(e) => setCodigo(e.target.value.toUpperCase())}
            size="small"
            fullWidth
            inputProps={{ style: { fontFamily: 'monospace' } }}
          />
          <Button
            variant="contained"
            disabled={buscando || !codigo.trim()}
            onClick={() => navigate(`/verificar/${encodeURIComponent(codigo.trim())}`)}
          >
            Verificar
          </Button>
        </Stack>

        {erro && <Alert severity="warning">{erro}</Alert>}

        {dados && (
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Chip color={COR_DO_DOCUMENTO[dados.status]} label={ROTULO_DO_DOCUMENTO[dados.status]} />
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                {dados.codigoDeVerificacao}
              </Typography>
            </Stack>
            <Typography variant="h6" sx={{ wordBreak: 'break-word' }}>
              {dados.titulo}
            </Typography>
            <Typography color="text.secondary">{dados.empresa}</Typography>
            <Typography variant="body2" color="text.secondary">
              Criado em {new Date(dados.criadoEm).toLocaleString('pt-BR')}
              {dados.concluidoEm && ` · concluído em ${new Date(dados.concluidoEm).toLocaleString('pt-BR')}`}
            </Typography>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Signatários
            </Typography>
            <Stack spacing={1}>
              {dados.signatarios.map((s, i) => (
                <Stack key={i} direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography sx={{ minWidth: 120 }}>{s.nome}</Typography>
                  <Chip size="small" color={COR_DO_SIGNATARIO[s.status]} label={ROTULO_DO_SIGNATARIO[s.status]} />
                  {s.assinadoEm && (
                    <Typography variant="body2" color="text.secondary">
                      {new Date(s.assinadoEm).toLocaleString('pt-BR')}
                    </Typography>
                  )}
                </Stack>
              ))}
            </Stack>

            <Divider sx={{ my: 2 }} />
            <Stack spacing={1.5}>
              <Hash rotulo="SHA-256 do documento original" valor={dados.hashOriginal} />
              {dados.hashFinal && <Hash rotulo="SHA-256 do documento assinado (com o protocolo)" valor={dados.hashFinal} />}
            </Stack>

            {dados.hashFinal && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Conferir um arquivo
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Escolha o PDF assinado que você recebeu. A conferência é feita neste aparelho, e o arquivo não é
                  enviado a lugar nenhum.
                </Typography>
                <Button variant="outlined" component="label">
                  Escolher PDF
                  <input hidden type="file" accept="application/pdf" onChange={(e) => void conferir(e.target.files?.[0])} />
                </Button>
                {conferencia && (
                  <Alert severity={conferencia.confere ? 'success' : 'error'} sx={{ mt: 2 }}>
                    {conferencia.confere
                      ? `"${conferencia.nome}" é exatamente o documento assinado registrado.`
                      : `"${conferencia.nome}" não é o documento assinado registrado: o arquivo foi alterado ou é outro.`}
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default VerificarAssinaturaPage;
